<template>
  <div
    class="node"
    :class="
      [
        selected(),
        node.name,
        hasWarning ? 'warning' : '',
        hasError ? 'error' : '',
      ] | kebab
    "
  >
    <div class="title">
      <img class="icon" src="../assets/icons/action.svg">
      <div class="title-text">
        <span>{{ titleText }}</span>
        <md-tooltip md-direction="top">Action: {{ titleText }}</md-tooltip>
      </div>

      <help-tooltip v-if="hasWarning" type="warning" :show-space="false">
        <span v-for="warning in warnings" :key="warning.message">
          {{ warning.message }}
          <br>
        </span>
      </help-tooltip>
      <help-tooltip v-if="hasError" type="error" :show-space="false">
        <span v-for="error in errors" :key="error.message">
          {{ error.message }}
          <br>
        </span>
      </help-tooltip>

      <div v-if="editor.isAdmin" class="nodeId">ID:{{ node.id }}</div>
    </div>
    <segmented-select
      :items="availableControls()"
      :value="state.activeControlType"
      segment-by="superCategory"
      placeholder="When..."
      :disabled="state.readonly"
      @select="control => updateActiveControl(control.value)"
    />

    <div v-for="input in inputs()" :key="input.key" class="input">
      <Socket
        v-socket:input="input"
        class="socket"
        type="input"
        :socket="input.socket"
      />
      <div v-show="!input.showControl()" class="input-title">
        {{ input.name }}
      </div>
      <div
        v-show="input.showControl()"
        v-control="input.control"
        class="input-control"
      />
    </div>

    <div
      v-for="control in controls()"
      :key="control.key"
      v-control="control"
      class="control"
    />

    <div class="sockets-container">
      <!-- This is so the outputs are placed at the end -->
      <div />
      <div v-for="output in outputs()" :key="output.key" class="output">
        <div class="output-title">{{ output.name }}</div>
        <Socket
          v-socket:output="output"
          class="socket"
          type="output"
          :socket="output.socket"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ReteVueRenderPlugin from 'rete-vue-render-plugin';
import findKey from 'lodash/findKey';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import intersection from 'lodash/intersection';
import toArray from 'lodash/toArray';
import sortBy from 'lodash/sortBy';

import HelpTooltip from '../../common/components/HelpTooltip';
import SegmentedSelect from '../components/SegmentedSelect.vue';
import ACTION_CONTROLS from '../ACTION_CONTROLS';
import { getControlForType, getControlDescriptionForType } from '../utils/getControlForType';
import getRecommendedControls from '../utils/getRecommendedControls';


export default {
  name: 'action',
  components: {
    Socket: ReteVueRenderPlugin.Socket,
    HelpTooltip,
    SegmentedSelect,
  },
  mixins: [ReteVueRenderPlugin.mixin],
  props: {},
  data() {
    return {
      ACTION_CONTROLS,
      state: {
        activeControlType: undefined,
        activeControl: null,
        readonly: false,
        hasInputConnection: true,
        hasOutputConnection: true,
      },
    };
  },
  computed: {
    controlDescriptor() {
      return this.ACTION_CONTROLS[this.state.activeControlType] || { label: 'Action' };
    },
    titleText() {
      return this.controlDescriptor.label;
    },
    hasWarning() {
      return this.warnings && this.warnings.length;
    },
    hasError() {
      return this.errors && this.errors.length;
    },
    isValid() {
      return this.state.hasOutputConnection;
    },
    warnings() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`
        ),
        'message'
      );
    },
    errors() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/errorsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`
        ),
        'message'
      );
    },
    chainableComponents() {
      return intersection(
        ['Check', 'Effect', 'Timer', 'EffectChain'],
        toArray(this.editor.components.keys())
      );
    },
  },
  created() {
    this.resetControls();
    const { data } = this.node;
    if (data.action) {
      this.updateActiveControl(data.action.type, false);
    }
    this.state.readonly = this.editor.readonly;
    this.editor.on('readonly', (readonly) => {
      this.state.readonly = readonly;
    });
  },
  beforeDestroy() {
    if (this.state.activeControl) {
      // Rete does not delete vue instances when node is destroyed
      this.state.activeControl._vue.$destroy();
    }
  },
  methods: {
    availableControls() {
      return sortBy(
        getRecommendedControls(
          this.editor.caseConf,
          ACTION_CONTROLS,
          this.editor.isGlobal
        ).map((control) => ({
          ...control,
          value: control.key,
          class: control.recommended ? 'control-option recommended' : 'control-option',
          // Most actions start with "When", since it's already in the dropdown
          // placeholder we strip it away in the dropdown label
          dropdownLabel: control.label.replace('When', '').trim(),
        })),
        (control) => ['Assess', 'Monitor', 'Treat', 'Miscellaneous'].indexOf(control.superCategory)
      );
    },
    resetControls() {
      if (this.node.controls.get('action')) {
        const currentControl = this.node.controls.get('action');
        this.node.removeControl(currentControl);
        // Rete does not delete vue instances when controls are removed
        if (currentControl._vue) {
          currentControl._vue.$destroy();
        }
      }
    },
    updateActiveControl(controlType, resetData = true) {
      if (controlType === this.state.activeControlType) {
        return;
      }
      this.state.activeControlType = controlType;
      this.resetControls();

      // For some reason removing a control and adding one in
      // the same stack frame causes the node to not update
      setTimeout(() => {
        const { activeControlType } = this.state;
        const { action } = this.node.data;
        const ActionControl = getControlForType(
          activeControlType,
          action,
          ACTION_CONTROLS
        );
        this.state.activeControl = new ActionControl(this.editor, 'action');

        // The selection dropdown won't display correctly when loading an action type
        // with multiple controls from saved node data. We need to look up the key,
        // and re-assign the activeControlType.
        if (!ACTION_CONTROLS[activeControlType]) {
          this.state.activeControlType = findKey(
            ACTION_CONTROLS,
            (a) => a.control === ActionControl
          );
        }

        if (resetData) {
          this.node.data = {};
        }

        this.node.addControl(this.state.activeControl);
        this.$forceUpdate();
        this.editor.trigger('process', this.node);
        if (resetData) {
          const actionControlDescription = getControlDescriptionForType(
            activeControlType,
            action,
            ACTION_CONTROLS
          );
          this.$analytics.event('Authoring Tool', 'action_selected', actionControlDescription && actionControlDescription.title);
        }
      });
    },
  },
};
</script>

<style scoped>
.node {
  background: rgb(48, 89, 201);
  border-radius: 10px;
  cursor: pointer;
  width: 300px;
  padding-bottom: 6px;
  box-sizing: content-box;
  position: relative;
  user-select: none;
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  box-sizing: border-box;
}
.node:hover {
  background: rgba(48, 89, 201, 0.8)
}

.node.highlighted {
  background: #FBD430;
  border-color: #FBD430;
}

.node.selected {
  box-shadow: 0 3px 6px 0 rgba(255, 255, 255, 0.7);
  border: solid 4px #ffffff;
}
.node .icon {
  width: 16px;
  height: 16px;
  display: inline;
}
.node .title {
  color: white;
  font-family: sans-serif;
  font-size: 18px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.node .title .title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  padding-left: 4px;
}

.node .nodeId {
  padding-left: 4px;
}
.sockets-container {
  display: flex;
  justify-content: space-between;
}
.node .socket.input {
  background: url("../assets/icons/active-input.svg");
  background-size: 100%;
  background-repeat: no-repeat;
  margin-left: 0;
}
.node .socket.output {
  background: url("../assets/icons/active-output.svg");
  background-size: 100%;
  background-repeat: no-repeat;
  margin-right: 0;
}
.node .socket {
  border: none;
}
.node .socket:hover {
  border: solid;
}
.node .output {
  text-align: right;
}
.node .input {
  text-align: left;
}
.node .input-title,
.node .output-title {
  vertical-align: middle;
  color: white;
  display: inline-block;
  font-family: sans-serif;
  font-size: 14px;
  margin: 6px;
  line-height: 24px;
}
.node .input-control {
  z-index: 1;
  width: calc(100% - 36px);
  vertical-align: middle;
  display: inline-block;
}
.node .control {
  padding: 6px 18px;
}
.node.error {
  border: solid 3px #f92c2d;
}
.node.warning {
  border: solid 3px #f2994a;
}

.node >>> .control-option:not(.recommended) {
  color:  #a9b3bc;
}
</style>
